import React from 'react';
import {
  TextField,
  Box,
  Typography,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Alert,
  Button,
} from '@mui/material';
import { ClearAll } from '@mui/icons-material';

interface ThirdPartyRiskData {
  ofacSanctions: string;
  ofacSanctionsDetails: string;
  angelRelationships: string;
  angelRelationshipsDetails: string;
  personalInfoAccess: string;
  personalInfoDetails: string;
  specialCircumstances: string;
  specialCircumstancesDetails: string;
}

interface ThirdPartyRiskFormProps {
  data: ThirdPartyRiskData;
  onChange: (data: ThirdPartyRiskData) => void;
  disabled?: boolean;
  errors?: { field: string; message: string }[];
}

const ThirdPartyRiskForm: React.FC<ThirdPartyRiskFormProps> = ({ data, onChange, disabled = false, errors = [] }) => {
  const getFieldError = (fieldName: string) => {
    return errors.find(err => err.field === fieldName)?.message;
  };
  const handleChange = (field: keyof ThirdPartyRiskData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    onChange({
      ...data,
      [field]: event.target.value,
    });
  };

  const handleClear = () => {
    onChange({
      ofacSanctions: '',
      ofacSanctionsDetails: '',
      angelRelationships: '',
      angelRelationshipsDetails: '',
      personalInfoAccess: '',
      personalInfoDetails: '',
      specialCircumstances: '',
      specialCircumstancesDetails: '',
    });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">B. Third Party Risk Profile Checklist</Typography>
        <Button
          variant="outlined"
          color="secondary"
          startIcon={<ClearAll />}
          onClick={handleClear}
          size="small"
        >
          Clear Form
        </Button>
      </Box>
      
      {/* <Alert severity="warning" sx={{ mb: 3 }}>
        Please note that all the fields must be completed or the request will be rejected.
      </Alert> */}

      <Alert severity="info" sx={{ mb: 3 }}>
        If you answer "YES" to any question in Part B below, this request may be routed to Legal for approval.
      </Alert>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <FormControl component="fieldset" sx={{ mt: 2}} disabled={disabled} error={!!getFieldError('ofacSanctions')}>
          <FormLabel component="legend">
            1. OFAC Sanctions Check
            <br />
            <strong>Before answering, search the supplier/vendor name using the OFAC Sanctions Search:</strong>
            <br />
            <a href="https://sanctionssearch.ofac.treas.gov/" target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2' }}>
              https://sanctionssearch.ofac.treas.gov/
            </a>
            <br /><br />
            Does the supplier/vendor (or its owners/management, if known) appear to match an OFAC sanctions listing?
          </FormLabel>
          <RadioGroup
            value={data.ofacSanctions}
            onChange={handleChange('ofacSanctions')}
            onFocus={(e) => e.preventDefault()}
          >
            <FormControlLabel value="YES" control={<Radio />} label="YES" />
            <FormControlLabel value="NO" control={<Radio />} label="NO" />
          </RadioGroup>
          {getFieldError('ofacSanctions') && (
            <Typography variant="caption" color="error" sx={{ mt: 1 }}>
              {getFieldError('ofacSanctions')}
            </Typography>
          )}
        </FormControl>

        <TextField
          fullWidth
          multiline
          rows={4}
          label="If YES, please add details here"
          value={data.ofacSanctionsDetails}
          onChange={handleChange('ofacSanctionsDetails')}
          variant="outlined"
          disabled={disabled}
          error={!!getFieldError('ofacSanctionsDetails')}
          helperText={getFieldError('ofacSanctionsDetails')}
        />

        <FormControl component="fieldset" sx={{ mt: 2 }} disabled={disabled} error={!!getFieldError('angelRelationships')}>
          <FormLabel component="legend">
            2. Are you aware of any familial or close personal relationships between Angel Studios and this supplier/vendor or anyone in the supplier’s management or ownership?
            <br />
            <em>(Familial relationships include spouse/partner, parent, child, sibling, or in-laws.)</em>
            <br /><br />
            If so, please contact Legal and describe below:
          </FormLabel>
          <RadioGroup
            value={data.angelRelationships}
            onChange={handleChange('angelRelationships')}
            onFocus={(e) => e.preventDefault()}
          >
            <FormControlLabel value="YES" control={<Radio />} label="YES" />
            <FormControlLabel value="NO" control={<Radio />} label="NO" />
          </RadioGroup>
          {getFieldError('angelRelationships') && (
            <Typography variant="caption" color="error" sx={{ mt: 1 }}>
              {getFieldError('angelRelationships')}
            </Typography>
          )}
        </FormControl>

        <TextField
          fullWidth
          multiline
          rows={4}
          label="If YES, please describe the relationships"
          value={data.angelRelationshipsDetails}
          onChange={handleChange('angelRelationshipsDetails')}
          variant="outlined"
          disabled={disabled}
          error={!!getFieldError('angelRelationshipsDetails')}
          helperText={getFieldError('angelRelationshipsDetails')}
        />

        <FormControl component="fieldset" sx={{ mt: 2 }} disabled={disabled} error={!!getFieldError('personalInfoAccess')}>
          <FormLabel component="legend">
            3. Will the third party process, transmit, access, or store any customer or employee personal data (e.g., names, email addresses, payment information, SSN, health information
            If so, please contact security@angel.com and include the details of that conversation below:
          </FormLabel>
          <RadioGroup
            value={data.personalInfoAccess}
            onChange={handleChange('personalInfoAccess')}
            onFocus={(e) => e.preventDefault()}
          >
            <FormControlLabel value="YES" control={<Radio />} label="YES" />
            <FormControlLabel value="NO" control={<Radio />} label="NO" />
          </RadioGroup>
          {getFieldError('personalInfoAccess') && (
            <Typography variant="caption" color="error" sx={{ mt: 1 }}>
              {getFieldError('personalInfoAccess')}
            </Typography>
          )}
        </FormControl>

        <TextField
          fullWidth
          multiline
          rows={4}
          label="If YES, please provide details"
          value={data.personalInfoDetails}
          onChange={handleChange('personalInfoDetails')}
          variant="outlined"
          disabled={disabled}
          error={!!getFieldError('personalInfoDetails')}
          helperText={getFieldError('personalInfoDetails')}
        />

        <FormControl component="fieldset" sx={{ mt: 2 }} disabled={disabled} error={!!getFieldError('specialCircumstances')}>
          <FormLabel component="legend">
            4. Do any of the following circumstances apply to this third party? If so, please contact Legal to obtain clearance
            and to add any additional language into the contract.
          </FormLabel>
          <Box sx={{ pl: 2, mb: 2 }}>
            <Typography variant="body2">
              - Did any government official recommend or require use of this third party?
            </Typography>
            <Typography variant="body2">
              - Is the anticipated contract value above $5,000,000?
            </Typography>
            <Typography variant="body2">
              - Will they represent Angel in any way to any government agency, entity, or official?
            </Typography>
            <Typography variant="body2">
              - Is the third party receiving a charitable donation from Angel?
            </Typography>
            <Typography variant="body2">
              - Is the third party a government agency, entity, or official?
            </Typography>
            <Typography variant="body2">
              - Is the third party a media company owned more than 50% by a government entity / official?
            </Typography>
          </Box>
          <RadioGroup
            value={data.specialCircumstances}
            onChange={handleChange('specialCircumstances')}
            onFocus={(e) => e.preventDefault()}
          >
            <FormControlLabel value="YES" control={<Radio />} label="YES" />
            <FormControlLabel value="NO" control={<Radio />} label="NO" />
          </RadioGroup>
          {getFieldError('specialCircumstances') && (
            <Typography variant="caption" color="error" sx={{ mt: 1 }}>
              {getFieldError('specialCircumstances')}
            </Typography>
          )}
        </FormControl>

        <TextField
          fullWidth
          multiline
          rows={4}
          label="If any of items above are answered Yes, please include the details below"
          value={data.specialCircumstancesDetails}
          onChange={handleChange('specialCircumstancesDetails')}
          variant="outlined"
          disabled={disabled}
          error={!!getFieldError('specialCircumstancesDetails')}
          helperText={getFieldError('specialCircumstancesDetails')}
        />
      </Box>
    </Box>
  );
};

export default ThirdPartyRiskForm;
